import { Button, Form, Col, Offcanvas, Row, Card } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import HelperService from "../../utility/HelperService";
import WebService from "../../utility/WebService";
import { Label } from "../../components/Label/Label";
import PlotDatePicker from "../../components/PlotDatePicker/PlotDatePicker";
import UploadFile from "../../components/UploadFile/UploadFile";
import { IoClose } from "react-icons/io5";
import DocIcon from "../../assets/images/doc-icon.svg";

interface PropData {
    isShow: boolean;
    isClose: Function;
    data: any;
}

interface formInterface {
    id: number
    uuid?: string
    title: string
    description?: string
    file_name?: string
    file_size?: string
    record_date: Date | null
    status?: string
    created_by?: number
    created_at?: Date
    updated_at?: Date
}

const AddDayBook = (props: PropData) => {
    const { handleSubmit, formState: { errors }, control, watch, reset, setValue } = useForm<formInterface>({});
    const watchAllFields = watch();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [previewUrl, setPreviewUrl] = useState<string>("");

    useEffect(() => {
        if (props.isShow) {
            if (props.data && !HelperService.isEmptyObject(props.data)) {
                setValue("id", props.data.id);
                setValue("uuid", props.data.uuid);
                setValue("title", props.data.title);
                setValue("description", props.data.description);
                setValue("file_name", props.data.file_name);
                setValue("file_size", props.data.file_size);
                setValue("record_date", props.data.record_date ? new Date(props.data.record_date) : null);
                setIsEdit(true);
            } else {
                reset({});
                resetForm();
                setIsEdit(false);
            }
        }
    }, [props.isShow, props.data]);

    // fetch a signed url so an image attachment can be shown as a thumbnail
    useEffect(() => {
        const fileName = watchAllFields.file_name;
        if (fileName && /(\.(jpg|jpeg|png|gif))$/i.test(fileName)) {
            WebService.postAPI({ action: "get/signed-url", body: { key: `day_book_files/${fileName}` } })
                .then((res: any) => { setPreviewUrl(res && res.signed_url ? res.signed_url : ""); })
                .catch(() => { setPreviewUrl(""); });
        } else {
            setPreviewUrl("");
        }
    }, [watchAllFields.file_name]);

    const handleFormSubmit = (data: formInterface) => {
        const url = isEdit ? `day-book/update` : "day-book/create";
        WebService[isEdit ? "putAPI" : 'postAPI']({
            action: url, body: data, id: "changePassword-btn"
        }).then((res: any) => {
            toast.success(res.message);
            closeModal(true);
        }).catch(() => { });
    };

    const closeModal = (e: boolean) => {
        reset();
        resetForm();
        props.isClose(e);
    };

    const resetForm = () => {
        reset({});
    };

    const onUploadFile = (data: any, name: string, size: string) => {
        uploadFile(data, size);
    };

    const uploadFile = (data: any, size: string) => {
        WebService.fileUploadAPI({ action: `upload/file`, key: "image", file: data, body: { type: "DAY_BOOK" }, id: "changePassword-btn" })
            .then(async (res: any) => {
                toast.success("File uploaded successfully");
                setValue("file_name", res.file_name);
                setValue("file_size", size);
            })
            .catch(async (error: any) => { });
    };

    const getSignedUrl = (fileName: string) => {
        WebService.postAPI({ action: "get/signed-url", body: { key: `day_book_files/${fileName}` } })
            .then((res: any) => {
                if (res && res.signed_url) {
                    window.open(res.signed_url, "_blank");
                } else {
                    toast.error("Failed to get signed URL");
                }
            })
            .catch((error: any) => { toast.error("Failed to get signed URL"); });
    };

    const onDeleteImage = () => {
        setValue("file_name", "");
        setValue("file_size", "");
    };

    return (
        <Offcanvas show={props.isShow} onHide={props.isClose} placement="end" className="offcanvas-large">
            <Offcanvas.Header closeButton><Offcanvas.Title>{isEdit ? "Edit Day Book" : "Add Day Book"}</Offcanvas.Title></Offcanvas.Header>
            <Offcanvas.Body className="form-style">
                <form className="form-style" onSubmit={handleSubmit(handleFormSubmit)}  >
                    <Row>
                        <Col lg={12} md={6} sm={12} className="mb-3">
                            {/* Title */}
                            <Controller
                                control={control}
                                name="title"
                                rules={{ required: "Please Enter Title" }}
                                render={({ field: { onChange, onBlur }, fieldState: { isTouched } }) => (
                                    <div className="mb-2">
                                        <Form.Label>Title</Form.Label><span className="text-danger">*</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Title"
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            value={watchAllFields.title}
                                        />
                                        {(errors["title"]?.message || Boolean(errors["title"]?.message) || (isTouched && !watchAllFields.title)) && (
                                            <div className="login-error">
                                                <Label title={errors.title?.message || "Please Enter Title."} modeError={true} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            />

                            {/* Record Date */}
                            <Controller
                                control={control}
                                name="record_date"
                                rules={{ required: true, }}
                                render={({ field: { onChange } }) => (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Record Date</Form.Label>
                                        <span className="text-danger">*</span>
                                        <div className="input-group mb-1">
                                            <div className="col-12 form-group">
                                                <PlotDatePicker
                                                    selected={watch("record_date") ? new Date(watch("record_date") as any) : null}
                                                    onChange={(date: any) => onChange(date)}
                                                    placeholderText="Select Date"
                                                />
                                                {errors.record_date && (<Label title={"Please Select Record Date."} modeError={true} />)}
                                            </div>
                                        </div>
                                    </Form.Group>
                                )}
                            />

                            {/* Description */}
                            <Controller
                                control={control}
                                name="description"
                                rules={{ required: false }}
                                render={({ field: { onChange, onBlur } }) => (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <div className="input-group mb-1 mt-1">
                                            <textarea
                                                rows={4}
                                                className="form-control ps-3 p-2"
                                                placeholder="Description"
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                value={watchAllFields.description || ""}
                                            />
                                        </div>
                                    </Form.Group>
                                )}
                            />

                            {/* Upload */}
                            <Form.Group className="mb-3">
                                <Form.Label className="d-block">Attachment</Form.Label>
                                {!watchAllFields.file_name && <UploadFile
                                    key={new Date().getTime()}
                                    onChange={(data: any, name: string, size: string) => { onUploadFile(data, name, size); }}
                                    acceptFileType={["jpg", "jpeg", "png", "pdf", "xlsx", "xls"]}
                                    message="Upload or Drag and Drop file here"
                                    acceptedSize={5}
                                />}

                                {(watchAllFields.file_name) &&
                                    <Card className={`border p-2 px-3 mb-0 h-auto`}>
                                        <div className="d-flex gap-3 align-items-center">
                                            <div className="me-1">
                                                <IoClose size={20} onClick={() => { onDeleteImage(); }} className="text-secondary opacity-50 hover-effect cursor-pointer close" />
                                            </div>
                                            <div>
                                                {previewUrl
                                                    ? <img src={previewUrl} width={56} height={56} alt={watchAllFields.file_name} className="rounded border object-fit-cover cursor-pointer" onError={() => setPreviewUrl("")} onClick={() => getSignedUrl(watchAllFields.file_name || "")} />
                                                    : <img src={DocIcon} width={40} height={40} alt="" />}
                                            </div>
                                            <div className="text-break">
                                                <p className="font-medium font-16 mb-0 cursor-pointer text-break" onClick={() => getSignedUrl(watchAllFields.file_name || "")}>
                                                    {watchAllFields.file_name}
                                                </p>
                                            </div>
                                        </div>
                                    </Card>}
                            </Form.Group>
                        </Col>

                    </Row>
                    <div className="text-center border-top">
                        <Button variant="dark" className="btn-brand-1 px-5 mt-3 mb-3 me-2" type="submit" id="changePassword-btn">{isEdit ? "Update" : "Add"}</Button>
                        <Button variant="light" className="btn-brand-1 px-5 mt-3 mb-3" type="button" onClick={() => closeModal(false)}>Close</Button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas >
    );
};

export default AddDayBook;
